import React, { Suspense } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { Toaster } from "sonner";

import useAppBootstrap from "./app/hooks/useAppBootstrap";
import { useRoutePrefetch } from "./app/hooks/useRoutePrefetch";
import ConnectionHealthBanner from "./components/connectionHealth/ConnectionHealthBanner";
import ErrorBoundary from "./components/ErrorBoundary";
import { FullScreenShim } from "./components/FullScreenProvider";
import ErrorPage from "./components/statePages/ErrorPage";
import FullPageLoader from "./components/statePages/FullPageLoader";
import WorkspaceNotFound from "./components/statePages/WorkspaceNotfound";
import {
  ErrorIcon,
  InfoIcon,
  SuccessIcon,
  WarningIcon,
} from "./components/ui/ToastIcons";
import { TooltipProvider } from "./components/ui/tooltip";
import CookieConsentBanner from "./features/cookieConsent/components/CookieConsentBanner";
import { isCookieConsentEnabledForLocation } from "./features/cookieConsent/cookieConsent.scope";
import { useSyncConsent } from "./features/cookieConsent/hooks/useSyncConsent";
import { UploadQueueOverlay } from "./features/intelliflow/components/UploadQueueCard";
import {
  MaintenanceProvider,
  MaintenanceRootGate,
} from "./features/maintenance";
import MaintenanceDowntimePage from "./features/maintenance/pages/MaintenanceDowntimePage";
import { useBrowserQueryParamSanitizer } from "./hooks/query/useBrowserQueryParamSanitizer";
import FeatureInitializer from "./pages/FeatureInitializer";
import Page404 from "./pages/Page404";
import DomainRouter from "./routers/DomainRouter";
import { useSentryScope } from "./sentry/useSentryScope";

import { useAppSelector } from "@/redux/store";
import { hasInvalidBrowserQueryParams } from "@/utils/form/queryParams";

const App: React.FC = () => {
  const { isLoading, isValidCompany, serviceError, subdomain, domainParts } =
    useAppBootstrap();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const hasInvalidQueryParams = hasInvalidBrowserQueryParams(searchParams);
  const userToken = useAppSelector((state) => state.user.userToken);
  const isCookieConsentEnabled = isCookieConsentEnabledForLocation({
    hostname: window.location.hostname,
    pathname,
  });

  useSentryScope();
  useBrowserQueryParamSanitizer();
  useRoutePrefetch();
  useSyncConsent(Boolean(userToken) && isCookieConsentEnabled);

  if (isLoading) {
    return <FullPageLoader />;
  }

  if (hasInvalidQueryParams) {
    return <Page404 />;
  }

  if (serviceError === "MAINTENANCE") {
    return <MaintenanceDowntimePage schedule={null} />;
  }

  if (serviceError === "SERVER") {
    return <ErrorPage />;
  }

  // ww5 domain must be validated whether its required or not
  if (!isValidCompany && subdomain !== "ww5") {
    return <WorkspaceNotFound url={subdomain} />;
  }

  return (
    <ErrorBoundary>
      <FullScreenShim>
        <FeatureInitializer>
          <TooltipProvider>
            <main className='relative overflow-x-hidden overflow-y-hidden'>
              <Toaster
                id='global'
                position='bottom-right'
                duration={2000}
                className='pointer-events-auto'
                icons={{
                  success: SuccessIcon,
                  info: InfoIcon,
                  warning: WarningIcon,
                  error: ErrorIcon,
                }}
              />
              <Suspense fallback={<FullPageLoader />}>
                <MaintenanceProvider>
                  <MaintenanceRootGate>
                    <DomainRouter
                      subdomain={subdomain}
                      domainParts={domainParts}
                    />
                  </MaintenanceRootGate>
                </MaintenanceProvider>
              </Suspense>
              <UploadQueueOverlay />
              <ConnectionHealthBanner />
              <CookieConsentBanner enabled={isCookieConsentEnabled} />
            </main>
          </TooltipProvider>
        </FeatureInitializer>
      </FullScreenShim>
    </ErrorBoundary>
  );
};

export default App;
